// components/breadcrumbs.tsx
import { ChevronRight } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/ui/style-helpers'
import { UrlParam } from '@/lib/url/url-param.hook'

interface BreadcrumbItem {
  label: string | React.ReactNode
  to?: string
  value?: string
  urlParamName?: UrlParam
  disabled?: boolean
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  separator?: React.ReactNode
  className?: string
  itemClassName?: string
  separatorClassName?: string
  onItemClick?: (item: BreadcrumbItem, index: number, event: React.MouseEvent) => void
}

const BreadcrumbSeparator: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  children,
  ...props
}) => (
  <span
    role='presentation'
    aria-hidden='true'
    data-testid='breadcrumb-separator'
    className={cn(
      'flex size-4 items-center justify-center text-main-foreground-tertiary',
      className,
    )}
    {...props}
  >
    {children}
  </span>
)

const BreadcrumbList: React.FC<React.OlHTMLAttributes<HTMLOListElement>> = ({
  className,
  ...props
}) => (
  <ol
    data-testid='breadcrumb-list'
    className={cn('flex min-w-0 items-center space-x-1', className)}
    {...props}
  />
)

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  separator = <ChevronRight className='size-4' />,
  className,
  itemClassName,
  separatorClassName,
  onItemClick,
  ...props
}) => {
  return (
    <nav aria-label='breadcrumb' data-testid='breadcrumbs' className={className} {...props}>
      <BreadcrumbList>
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1
          const isDisabled = !!item.disabled
          const isLink = !!item.to && !isDisabled && !isCurrent

          const handleClick = (event: React.MouseEvent) => {
            if (isDisabled || isCurrent) {
              event.preventDefault()
              return
            }

            if (onItemClick) {
              onItemClick(item, index, event)
            }
          }

          const content = isLink ? (
            <Link
              to={item.to!}
              onClick={handleClick}
              data-testid={`breadcrumb-item-${item.value ?? 'unknown'}`}
              className={cn(
                'inline-flex items-center text-main-foreground-tertiary !text-body-default',
                'cursor-pointer hover:text-main-foreground-primary',
                itemClassName,
              )}
              aria-current={isCurrent ? 'page' : undefined}
            >
              {item.label}
            </Link>
          ) : (
            <span
              onClick={handleClick}
              data-testid={`breadcrumb-item-${item.value ?? 'unknown'}`}
              className={cn(
                'inline-flex cursor-pointer items-center !text-body-default',
                {
                  'block min-w-0 truncate text-main-foreground-primary !text-label-default':
                    isCurrent,
                  'cursor-not-allowed text-main-foreground-disabled': isDisabled,
                  'text-main-foreground-tertiary': !isCurrent && !isDisabled,
                  'cursor-pointer': !!onItemClick && !isDisabled,
                },
                itemClassName,
              )}
              aria-current={isCurrent ? 'page' : undefined}
              aria-disabled={isDisabled || undefined}
            >
              {item.label}
            </span>
          )

          return (
            <li
              key={item.value ?? index}
              className={cn('flex items-center space-x-1.5', { 'min-w-0': isCurrent })}
            >
              {content}

              {!isCurrent && (
                <BreadcrumbSeparator className={separatorClassName}>
                  {separator}
                </BreadcrumbSeparator>
              )}
            </li>
          )
        })}
      </BreadcrumbList>
    </nav>
  )
}
